import { RichTextEditor } from '@/components/commons/minimal-tiptap';
import TextEditorMinimal from '@/components/commons/minimal-tiptap/minimal-editor';
import FacebookIcon from '@/components/icons/facebook-icon';
import InstagramIcon from '@/components/icons/instagram-icon';
import TiktokIcon from '@/components/icons/tiktok-icon';
import XTwitterIcon from '@/components/icons/x-icon';
import YouTubeIcon from '@/components/icons/youtube-icon';
import {
  ContentPopupMakerType,
  PopupMakerElementDataType,
} from '@/components/sections/builder/elements/body/popup-maker/type';
import { FooterElementDataType } from '@/components/sections/builder/elements/footer/footer/types';
import InputTab from '@/components/sections/builder/settings/input-tab';
import PaddingSetting from '@/components/sections/builder/settings/padding-setting';
import { ElementProps } from '@/components/sections/builder/types';
import { Checkbox } from '@/components/ui/checkbox';
import useMediaQuery from '@/hooks/useMediaQuery';
import { cn } from '@/lib/utils';
import { useBuilderStore } from '@/stores/builder';
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  AlignVerticalJustifyCenter,
  AlignVerticalJustifyEnd,
  AlignVerticalJustifyStart,
  TableOfContents,
} from 'lucide-react';
import Link from 'next/link';
import React from 'react';
import { shallow } from 'zustand/shallow';

const defaultData: ContentPopupMakerType = {
  name: 'Content',
  alignment: 'left',
  position: 'start',
  title: 'Title Popup',
  titleSize: 'small',
  content:
    "Lorem Ipsum is simply dummy text of the printing and typesetting industry. Lorem Ipsum has been the industry's standard dummy text ever since the 1500s, when an unknown printer took a galley of type and scrambled it to make a type specimen book.",
  showMediaSosial: false,
  padding: {
    bottom: 10,
    top: 10,
    left: 10,
    right: 10,
  },
};

const ElementContent = ({ element, preview }: ElementProps) => {
  const data = element.data as ContentPopupMakerType;
  const parentData = element.parentData as PopupMakerElementDataType;
  const elements = element.elements;
  const isMobile = useMediaQuery(962);

  const contentRef = React.useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = React.useState(false);

  const builder = useBuilderStore(
    (state) => ({
      theme: state.theme,
      elements: state.elements,
      setElementData: state.setElementData,
    }),
    shallow
  );

  const elementContent = preview ? builder.elements : elements;
  const footerElement = elementContent?.find((e) => e.group === 'footer');
  const dataFooter = footerElement?.data as FooterElementDataType;

  const listSosmed = [
    {
      icon: <FacebookIcon className='size-5' />,
      href: dataFooter?.facebookUrl,
    },
    {
      icon: <InstagramIcon className='size-5' />,
      href: dataFooter?.instagramUrl,
    },
    {
      icon: <XTwitterIcon className='size-4' />,
      href: dataFooter?.twitterUrl,
    },
    {
      icon: <TiktokIcon className='size-5' />,
      href: dataFooter?.tiktokUrl,
    },
    {
      icon: <YouTubeIcon className='size-5' />,
      href: dataFooter?.youtubeUrl,
    },
  ];

  React.useEffect(() => {
    const el = contentRef.current;
    if (el) {
      setIsOverflowing(el.scrollHeight > el.clientHeight);
    }
  }, [data]);

  return (
    <>
      <div
        ref={contentRef}
        className={cn(
          'flex h-full w-full flex-col md:overflow-y-auto',
          isMobile ? 'p-3' : 'padding-content-popup-maker text-ce',
          isOverflowing ? '!justify-start' : 'justity-content-popup-maker',
          'text-content-popup-maker'
        )}
      >
        {data?.title && (
          <div
            className={cn(
              'rich-text-content-popup-maker mb-5 leading-6 tracking-[-1.1%]',
              data.titleSize === 'small' && 'text-sm sm:text-base',
              data.titleSize === 'medium' && 'text-base sm:text-lg',
              data.titleSize === 'large' && 'text-lg sm:text-xl'
            )}
            dangerouslySetInnerHTML={{ __html: data.title || '' }}
          />
        )}
        <RichTextEditor
          key={data.content}
          content={data?.content || ''}
          editable={false}
          editorProps={{
            attributes: {
              class: 'prose lg:prose-lg prose-blue dark:prose-invert',
            },
          }}
        />
        {data.showMediaSosial && (
          <div className='mt-6 flex w-full items-center justify-center gap-3 p-2 lg:gap-5'>
            {listSosmed.map(
              (sosmed, idx) =>
                sosmed.href && (
                  <Link key={idx} href={sosmed.href} target='_blank'>
                    {sosmed.icon}
                  </Link>
                )
            )}
          </div>
        )}
      </div>

      <style jsx global>
        {`
          .padding-content-popup-maker {
            padding: ${data.padding?.top ?? defaultData.padding?.top}px
              ${data.padding?.right ?? defaultData.padding?.right}px
              ${data.padding?.bottom ?? defaultData.padding?.bottom}px
              ${data.padding?.left ?? defaultData.padding?.left}px;
          }
          .text-content-popup-maker {
            text-align: ${data.alignment || defaultData.alignment};
          }

          @media (min-width: 768px) {
            .justity-content-popup-maker {
              justify-content: ${data.position || defaultData.position};
            }
          }

          .rich-text-content-popup-maker a,
          .rich-text-content-popup-maker .link {
            color: ${parentData.colorSchemeSection?.btnSolidBackgroud || 'blue'} !important;
            text-decoration: underline !important;
          }

          .prose strong,
          .prose b {
            color: inherit !important;
          }

          .rich-text-content-popup-maker strong,
          .rich-text-content-popup-maker b {
            color: inherit !important;
          }
        `}
      </style>
    </>
  );
};

const ElementSetting = ({ element }: ElementProps) => {
  const data = element.data as ContentPopupMakerType;
  const builder = useBuilderStore(
    (state) => ({
      setChildData: state.setChildData,
    }),
    shallow
  );

  return (
    <div>
      <div className='mb-4 flex flex-col gap-1'>
        <h3 className='mb-2'>Alignment</h3>
        <InputTab
          defaultValue={data.alignment || defaultData.alignment}
          options={[
            { label: <AlignLeft className='h-4 w-4' />, value: 'left' },
            { label: <AlignCenter className='h-4 w-4' />, value: 'center' },
            { label: <AlignRight className='h-4 w-4' />, value: 'right' },
          ]}
          onChange={(v) => {
            builder.setChildData(element.parentId as string, element.id, {
              alignment: v,
            });
          }}
        />
        <InputTab
          defaultValue={data.position || defaultData.position}
          options={[
            { label: <AlignVerticalJustifyStart className='h-4 w-4' />, value: 'start' },
            { label: <AlignVerticalJustifyCenter className='h-4 w-4' />, value: 'center' },
            { label: <AlignVerticalJustifyEnd className='h-4 w-4' />, value: 'end' },
          ]}
          onChange={(v) => {
            builder.setChildData(element.parentId as string, element.id, {
              position: v,
            });
          }}
        />
      </div>
      <div className='mb-4'>
        <h3 className='mb-2'>Title Popup</h3>
        <TextEditorMinimal
          value={data.title as string}
          onChange={(val) =>
            builder.setChildData(element.parentId as string, element.id, { title: val })
          }
          output='html'
          editorProps={{
            attributes: {
              class: 'prose lg:prose-lg prose-blue dark:prose-invert p-2',
            },
          }}
          className='mb-2 min-h-32 w-full '
          editorContentClassName='overflow-y-auto'
          editable
        />
      </div>
      <div className='mb-4'>
        <h3 className='mb-2'>Title Size</h3>
        <InputTab
          defaultValue={data.titleSize || defaultData.titleSize}
          options={[
            { label: 'Small', value: 'small' },
            { label: 'Medium', value: 'medium' },
            { label: 'Large', value: 'large' },
          ]}
          onChange={(v) => {
            builder.setChildData(element.parentId as string, element.id, {
              titleSize: v,
            });
          }}
        />
      </div>
      <div className='mb-3'>
        <h3 className='mb-2'>Content Popup</h3>
        <TextEditorMinimal
          value={data.content as string}
          onChange={(val) =>
            builder.setChildData(element.parentId as string, element.id, { content: val })
          }
          output='html'
          editorProps={{
            attributes: {
              class: 'prose lg:prose-lg prose-blue dark:prose-invert p-2',
            },
          }}
          className='mb-2 max-h-56 w-full '
          editorContentClassName='overflow-auto h-full'
          editable
          enableTextColor
        />
      </div>
      <div className='mt-4 flex items-center'>
        <Checkbox
          id='showMediaSosial'
          checked={data.showMediaSosial}
          onCheckedChange={(val) =>
            builder.setChildData(element.parentId as string, element.id, {
              showMediaSosial: !!val,
            })
          }
          className='mr-4'
        />
        <label htmlFor='stickyProductDetail'>Show sosial media</label>
      </div>
      <span className='mt-2 flex text-[11px] text-gray-500'>
        Add link sosmed in theme global -&gt; Sosial Media
      </span>
      <PaddingSetting
        value={data.padding}
        hideLeft={false}
        hideRight={false}
        onChange={(v) =>
          builder.setChildData(element.parentId as string, element.id, {
            padding: v,
          })
        }
      />
    </div>
  );
};

const ElementPreviewIcon = TableOfContents;

const ContentPopupMaker = {
  type: 'content_popup',
  group: 'popup_maker',
  defaultData,
  Element: ElementContent,
  Setting: ElementSetting,
  PreviewIcon: ElementPreviewIcon,
  Icon: ElementPreviewIcon,
  childs: [],
  maxEntries: 1,
};

export default ContentPopupMaker;
